import React from 'react';
import { AnimatePresence } from 'framer-motion';
import { formatMultiplier } from '../utils/countModeHourlyPremiumUtils';
import { SurchargeMethodPanel } from '@/components/config_forms/shared/surcharge/SurchargeMethodPanel';
import { useMethodSwapAnimation } from '@/components/config_forms/shared/surcharge/useMethodSwapAnimation';
import { ViolationSpans, type ViolationSpan } from '@/components/config_forms/shared/help-graphics/timeline/TimelinePrimitives';

const OT_SPLIT_BOUNDARY_PCT = 60;
const OT_SPLIT_LEFT_WIDTH = `${OT_SPLIT_BOUNDARY_PCT}%`;
const OT_SPLIT_RIGHT_WIDTH = `${100 - OT_SPLIT_BOUNDARY_PCT}%`;

export interface MealHourlyPremiumOTSplitProps {
  premiumType: 'fixed' | 'percentage';
  premiumValue: number;
  premiumBasis: 'base_rate' | 'ot_inclusive';
  ladder: Array<{
    code: string;
    name: string;
    multiplier: number;
    thresholdHours: number | null;
  }>;
}

export const MealHourlyPremiumOTSplit: React.FC<MealHourlyPremiumOTSplitProps> = ({
  premiumType,
  premiumValue,
  premiumBasis,
  ladder,
}) => {
  if (premiumBasis !== 'ot_inclusive') return null;
  if (ladder.length < 2) return null;
  if (ladder[1].multiplier <= ladder[0].multiplier) return null;

  const baseTier = ladder[0];
  const otTier = ladder[1];

  return (
    <div
      className="mt-3"
      data-testid="meal-hourly-premium-ot-split"
      data-anim-audit="meal-hourly-premium-ot-split"
      data-anim-audit-state={`${premiumType}|${premiumBasis}`}
    >
      <div>
        {/* Violation label */}
        <div className="text-center mb-1">
          <span
            className="font-label text-[10px] sm:text-[12px] uppercase tracking-[1px]"
            style={{ color: 'color-mix(in srgb, var(--primary-text), transparent 20%)' }}
          >
            Violation
          </span>
        </div>

        {/* Tier spans — reuse ViolationSpans for consistent styling */}
        <ViolationSpans
          spans={[
            {
              id: 'base-tier',
              label: baseTier.name,
              variant: 'v1',
              leftPct: 0,
              rightPct: 100 - OT_SPLIT_BOUNDARY_PCT,
            },
            {
              id: 'ot-tier',
              label: otTier.name,
              variant: 'v1',
              leftPct: OT_SPLIT_BOUNDARY_PCT,
              rightPct: 0,
            },
          ] satisfies ViolationSpan[]}
          stacked={false}
        />

        {/* Split bar: height matches the main timeline's zone row (h-14 = 56px)
            so the two graphics share a visual rhythm. */}
        <div
          className="relative h-14 rounded-[0.25rem] overflow-hidden"
          style={{ boxShadow: 'inset 0 0 0 1px color-mix(in srgb, var(--primary), transparent 70%)' }}
        >
          <div
            className="absolute top-0 bottom-0 left-0 bg-primary/[0.06] flex flex-col items-center justify-center gap-1"
            style={{ width: OT_SPLIT_LEFT_WIDTH }}
            data-testid="ot-split-left-region"
          >
            <span
              className="font-mono text-[10px] sm:text-[12px] font-semibold text-center leading-tight text-muted-foreground"
            >
              {premiumType === 'fixed'
                ? `$${premiumValue}/hr × ${formatMultiplier(baseTier.multiplier)}`
                : <>{/* Single line on desktop, wrapped on mobile */}
                    <span className="hidden sm:inline">hourly premium × {formatMultiplier(baseTier.multiplier)}</span>
                    <span className="sm:hidden">hourly premium<br />× {formatMultiplier(baseTier.multiplier)}</span>
                  </>}
            </span>
          </div>
          <div
            className="absolute top-0 bottom-0"
            style={{
              left: OT_SPLIT_LEFT_WIDTH,
              width: '2px',
              transform: 'translateX(-1px)',
              background:
                'linear-gradient(to bottom, color-mix(in srgb, var(--color-foreground), transparent 70%), color-mix(in srgb, var(--color-foreground), transparent 90%))',
            }}
            data-testid="ot-split-divider"
          />
          <div
            className="absolute top-0 bottom-0 right-0 bg-primary/[0.12] flex flex-col items-center justify-center gap-1"
            style={{ width: OT_SPLIT_RIGHT_WIDTH }}
            data-testid="ot-split-right-region"
          >
            <span
              className="font-mono text-[10px] sm:text-[12px] font-semibold text-center leading-tight text-foreground"
            >
              {premiumType === 'fixed'
                ? `$${premiumValue}/hr × ${formatMultiplier(otTier.multiplier)}`
                : <>{/* Single line on desktop, wrapped on mobile */}
                    <span className="hidden sm:inline">hourly premium × {formatMultiplier(otTier.multiplier)}</span>
                    <span className="sm:hidden">hourly premium<br />× {formatMultiplier(otTier.multiplier)}</span>
                  </>}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MealHourlyPremiumOTSection: React.FC<MealHourlyPremiumOTSplitProps> = ({
  premiumType,
  premiumValue,
  premiumBasis,
  ladder,
}) => {
  const hasTierSplit =
    ladder.length >= 2
    && ladder[1].multiplier > ladder[0].multiplier;

  const showOTDetails = premiumBasis === 'ot_inclusive';
  const panelKey = showOTDetails ? 'ot-visible' : 'ot-hidden';
  const {
    containerStyle,
    containerRef,
    activePanelRef,
    fadeDuration,
  } = useMethodSwapAnimation(panelKey);

  if (!hasTierSplit) return null;

  return (
    <div
      ref={containerRef}
      className="overflow-hidden"
      style={containerStyle}
      data-anim-audit="hourly-premium-ot-section-container"
      data-anim-audit-open={showOTDetails ? 'true' : 'false'}
    >
      <div className="relative grid">
        <AnimatePresence mode="sync" initial={false}>
          {showOTDetails ? (
            <SurchargeMethodPanel
              key="ot-visible"
              activePanelRef={activePanelRef}
              transitionDuration={fadeDuration}
            >
              <div data-anim-audit="hourly-premium-ot-section-panel" data-anim-audit-open="true">
                <MealHourlyPremiumOTSplit
                  premiumType={premiumType}
                  premiumValue={premiumValue}
                  premiumBasis={premiumBasis}
                  ladder={ladder}
                />
              </div>
            </SurchargeMethodPanel>
          ) : (
            <SurchargeMethodPanel
              key="ot-hidden"
              activePanelRef={activePanelRef}
              transitionDuration={fadeDuration}
            >
              <div
                className="h-0 overflow-hidden"
                data-anim-audit="hourly-premium-ot-section-panel"
                data-anim-audit-open="false"
                aria-hidden
              />
            </SurchargeMethodPanel>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
